import type { UserAddress, UserAddressFieldType, UserAddressNew } from '../entities'
import { analytics } from '@/tools/analytics'
import type {
  AddressDetailsSourceValues,
  AddressSearchSourceValues,
  AddressSourceValues,
} from '@/tools/analytics/events/domains/address-book'
import type { PickAnalyticsEventAttributes } from '@/tools/analytics/types'

type AddressCheckEvent = PickAnalyticsEventAttributes<'AddressCheck'>
type AddressDetailsEvent = Omit<PickAnalyticsEventAttributes<'AddressDetails'>, 'isAddressFaulty'>
type AddressLandingOptionClickedEvent = PickAnalyticsEventAttributes<'AddressLandingOptionClicked'>

export type AddressCreationSucceededPayload = Omit<
  PickAnalyticsEventAttributes<'AddressCreationSucceeded'>,
  'addressType' | 'entrySource' | 'source'
>

interface AddressCreationPayload {
  address: UserAddress | UserAddressNew
  deviceLocation: boolean
  hasRefinedEntrance: boolean
  addressFormTrigger: AddressDetailsSourceValues
}

interface AddressCheckPayload {
  address: UserAddressNew
  addressStatusSuccessful: boolean
  checkType: AddressCheckEvent['checkType']
  query: AddressCheckEvent['query']
}

export const AddressFlowLandingScreenEvent = (
  isGpsEnabled: boolean,
  entrySource: AddressSearchSourceValues
) =>
  analytics.trackEvent('AddressFlowLandingScreen', {
    source: entrySource,
    isGpsEnabled,
    categoryId: null,
  })

export const addressLandingOptionClickedEvent = (
  option: AddressLandingOptionClickedEvent['addressOption']
) =>
  analytics.trackEvent('AddressLandingOptionClicked', {
    addressOption: option,
    source: 'AddressLandingScreen',
  })

export const addressCheckEvent = (payload: AddressCheckPayload) =>
  analytics.trackEvent('AddressCheck', {
    coordinatesLatitude: payload.address?.latitude,
    coordinatesLongitude: payload.address?.longitude,
    addressStatusSuccessful: payload.addressStatusSuccessful,
    checkType: payload.checkType,
    query: payload.query,
    generatedAddress: payload.address?.addressLine,
  })

export const addressDetailsEvent = (addressDetailsPayload: AddressDetailsEvent) =>
  analytics.trackEvent('AddressDetails', {
    ...addressDetailsPayload,
    isAddressFaulty: false,
  })

const isUserAddress = (address: UserAddress | UserAddressNew): address is UserAddress =>
  'id' in address

const getIsFieldMissing = (
  address: UserAddress | UserAddressNew,
  fieldType: UserAddressFieldType
) => {
  const field = address.fields.find(({ type }) => type === fieldType)
  return field?.value === ''
}
const getAddressEventData = (
  address: UserAddress | UserAddressNew,
  isBrowserLocationEnabled: boolean,
  hasRefinedEntrance: boolean,
  addressFormTrigger: AddressDetailsSourceValues
): AddressCreationSucceededPayload => ({
  isGpsEnabled: isBrowserLocationEnabled,
  isAddressRefined: hasRefinedEntrance,
  coordinatesLatitude: address.latitude,
  coordinatesLongitude: address.longitude,
  addressLine: address.addressLine,
  addressSource: addressFormTrigger ?? 'PinOnMap',
  addressId: isUserAddress(address) ? address.id : null,
  isStreetNameMissing: getIsFieldMissing(address, 'STREET_NAME'),
  isStreetNumberMissing: getIsFieldMissing(address, 'STREET_NUMBER'),
  isFloorMissing: getIsFieldMissing(address, 'FLOOR_NUMBER'),
  isDoorMissing: getIsFieldMissing(address, 'DOOR_NUMBER'),
  isAdditionalInformationMissing: getIsFieldMissing(address, 'ADDITIONAL_INFORMATION'),
  addressLabel: address.tag ?? null,
  addressKind: address.kind,
})

export const addressCreationSucceededEvent = (
  addressPayload: AddressCreationPayload,
  source: AddressSourceValues,
  entrySource: AddressSearchSourceValues
) =>
  analytics.trackEvent('AddressCreationSucceeded', {
    ...getAddressEventData(
      addressPayload.address,
      addressPayload.deviceLocation,
      addressPayload.hasRefinedEntrance,
      addressPayload.addressFormTrigger
    ),
    source,
    addressType: 'Custom',
    entrySource,
  })
